import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@proerp/ui';

/**
 * Wiederkehrende Bausteine der Portal-Seiten (ADR 0085). Sie stehen hier
 * einmal, damit Übersicht, Bedarfsvorschau und Montagen nicht drei leicht
 * verschiedene Kopfzeilen und drei leicht verschiedene Fehlermeldungen
 * bekommen — genau daran erkennt man zusammengestückelte Oberflächen.
 */

/** Kopfzeile einer Seite: Kennzeichnung, Titel, ein Satz Erklärung. */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && (
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {eyebrow}
          </p>
        )}
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

/** Abschnitt mit Überschrift — die Karte des Design-Systems, einmal verpackt. */
export function SectionCard({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription className="mt-1">{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * Ladezustand. Bewusst mit Text statt nur einem Kringel: Ein Spinner ohne
 * Beschriftung sagt nicht, worauf man wartet (CLAUDE.md §9).
 */
export function LoadingNote({ children = 'Daten werden geladen …' }: { children?: React.ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Spinner aria-hidden />
      {children}
    </p>
  );
}

/**
 * Fehlerzustand mit Wiederholen. Nie schweigend scheitern — und nie ohne einen
 * Weg zurück (CLAUDE.md §9).
 */
export function ErrorCard({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <p className="text-sm text-foreground">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unbekannter Fehler'}
      </p>
      <Button type="button" className="mt-4" onClick={onRetry}>
        Erneut versuchen
      </Button>
    </div>
  );
}

/** Leerzustand mit Symbol und Erklärung, warum hier nichts steht. */
export function EmptyNote({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <p>{children}</p>
    </div>
  );
}
